<template>
	<component :is="wrapper" :class="['eb-textarea', {'eb-textarea-expand': width === 'expand'}]">
		<EbLabel :forId="uid" :required="required" :infoIcon="infoIcon" :helpText="helpText" :text="label">
			<textarea
			:rows="rows"
			:cols="cols"
			:disabled="disabled"
			:class="[state, background ? 'eb-textarea-simple' : 'eb-textarea-solid']"
			:placeholder="placeholder"
			@input="onInput($event.target.value)"
			@focus="onFocus($event.target.value)"
			:value="value"
			:required="required"
			:aria-label="label"
			:maxlength="maxcharacter"
			:id="uid"
			/>
		</EbLabel>
		<p v-if="maxcharacter" :class="['eb-textarea-remain-text', this.value.length > this.maxcharacter ? 'eb-message-error' : '']">{{ remaincharactersText }}</p>
	</component>
</template>
<script>
/**
* Textareas are used to allow users to provide text input when the expected
* input is long. Textarea has a range of options. For shorter input,
* use the `Input` element.
*/
export default {
	name: "EbTextarea",
	status: "ready",
	release: "1.0.0",
	props: {
		/**
		* Text value of the form textarea.
		*/
		value: {
			type: String,
			default: null,
		},
		/**
		* The placeholder value for the form textarea.
		*/
		placeholder: {
			type: String,
			default: null,
		},
		/**
		* The label of the form textarea.
		*/
		label: {
			type: String,
			default: null,
		},
		/**
		* The html element name used for the wrapper.
		* @values div, section
		*/
		wrapper: {
			type: String,
			default: "div",
			validator: value => {
				return value.match(/(div|section)/)
			},
		},
		/**
		* The width of the form textarea.
		* @values auto, expand
		*/
		width: {
			type: String,
			default: "auto",
			validator: value => {
				return value.match(/(auto|expand)/)
			},
		},
		/**
		* The background field.
		* @values solid, simple
		*/
		background: {
			type: Boolean,
			default: false,
		},
		rows: {
			type: Number,
		},
		cols: {
			type: Number,
		},
		/**
		* Whether the form textarea is disabled or not.
		*/
		disabled: {
			type: Boolean,
			default: false,
		},
		/**
		* Manually trigger various states of the textarea.
		* @values hover, active, focus
		*/
		state: {
			type: String,
			default: null,
			validator: value => {
				return value.match(/(hover|active|focus)/)
			},
		},
		helpText: {
			type: String,
		},
		required: {
			type: Boolean,
			default: false,
		},
		infoIcon: {
			type: Boolean,
			default: false,
		},
		maxcharacter: {
			type: Number,
			default: null,
		},
	},
	data() {
		return {
			uid: null
		}
	},
	mounted() {
		this.uid = `uid_${this._uid}`
	},
	computed: {
		remaincharactersText: function() {
			let tmp = this.maxcharacter - this.value.length
			return tmp + " caractères restants"
		},
	},
	methods: {
		onInput(value) {
			this.$emit("input", value)
		},
		onFocus(value) {
			this.$emit("focus", value)
		},
	},
}
</script>
<style lang="scss" scoped>
.eb-textarea {
	@include stack-space($eb-space-m);
	label {
		cursor: pointer;
		display: block;
		@include stack-space($eb-space-s);
	}
	textarea {
		@include inset-space($eb-space-s);
		display: block;
		margin-top: $eb-space-default;
		transition: all 0.2s ease;
		-webkit-appearance: none;
		appearance: none;
		resize: vertical;
		min-height: $eb-tappable-square;
		font-size: $eb-font-size-xs;
		background: $white;
		border-width: $eb-border-default;
		border-style: solid;
		box-shadow: box-shadow-size(1);
		letter-spacing: normal;
		width: 100%;
		&::-webkit-input-placeholder {
			-webkit-font-smoothing: antialiased;
			font-size: $eb-font-size-xs;
			font-style: italic;
			font-weight: $eb-font-weight-regular;
			color: $eb-color-gray-700;
			opacity: 1;
		}
		&:-ms-input-placeholder {
			font-size: $eb-font-size-xs;
			font-style: italic;
			color: $eb-color-gray-700;
			opacity: 1;
			font-weight: $eb-font-weight-regular;
		}
		&::-moz-placeholder {
			-moz-osx-font-smoothing: grayscale;
			opacity: 1;
			font-size: $eb-font-size-xs;
			font-style: italic;
			color: $eb-color-gray-700;
			font-weight: $eb-font-weight-regular;
		}
		&:hover,
		&.hover {
			outline: $eb-border-default solid $eb-color-blue-secondary;
		}
		&:focus,
		&.focus {
			transition: box-shadow 0.2s ease;
			outline: $eb-border-xl solid $eb-color-blue-secondary;
			box-shadow: box-shadow-size(0);
		}
		&[disabled] {
			-webkit-font-smoothing: antialiased;
			-moz-osx-font-smoothing: grayscale;
			cursor: not-allowed;
			background-color: $eb-color-gray-100;
			box-shadow: none;
			border-color: $eb-color-gray-600;
			opacity: 1;
		}
		&:invalid {
			background-color: $eb-color-error-light;
			border: $eb-border-m solid $eb-color-error;
			color: $eb-color-error-dark;
			&:hover {
				outline-color: $eb-color-error;
			}
			&:focus {
				outline-width: $eb-border-l;
				outline-color: $eb-color-error;
			}
		}
	}
	.eb-textarea-solid {
		background: $eb-color-blue-lighter;
		border-color: $eb-color-blue-secondary;
	}
	.eb-textarea-simple {
		background: $white;
		border-color: $eb-color-gray-600;
	}
	.eb-textarea-remain-text {
		display: block;
		font-style: italic;
		font-size: $eb-font-size-xxs;
		margin-top: $eb-space-default;
	}
	
	&-expand.eb-textarea textarea {
		width: 100%;
	}
	
	@include respond-to("medium-up") {
		textarea {
			width: auto;
		}
	}
}
</style>
